import React from 'react';
import { Link } from 'react-router-dom';
import { Mic, Calendar, FileText, Users, Phone, Clock, Stethoscope, Cloud } from 'lucide-react';
import Footer from '../components/Footer';
import { usePageTitle } from '../hooks/usePageTitle';
import { trackAppDownload, trackGetStarted } from '../utils/analytics';
import { APP_NAME } from '../config/branding';

const IPhoneApp = () => {
  usePageTitle(`iPhone App - ${APP_NAME} Mobile for Veterinarians`);

  const features = [
    {
      icon: <FileText className="w-8 h-8" />,
      title: 'AI-Powered SOAP Notes',
      description: 'Record visit audio and get instant, AI-generated SOAP notes. Save time on documentation.',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      title: 'Appointment Management',
      description: 'View today\'s schedule, track completed visits, and manage your daily appointments on the go.',
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: <Mic className="w-8 h-8" />,
      title: 'Voice Recording',
      description: 'High-quality audio recording during consultations with automatic cloud backup and transcription.',
      color: 'bg-orange-100 text-orange-600'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Pet Owner Profiles',
      description: 'Access complete pet and owner information, medical history, and contact details instantly.',
      color: 'bg-indigo-100 text-indigo-600'
    },
    {
      icon: <Phone className="w-8 h-8" />,
      title: 'AI Front Desk',
      description: 'Monitor AI phone calls, listen to recordings, and see how your voice agent handles client calls.',
      color: 'bg-pink-100 text-pink-600'
    },
    {
      icon: <Stethoscope className="w-8 h-8" />,
      title: 'Medical Records',
      description: 'Review comprehensive medical records, treatment plans, diagnoses, and medications on your mobile device.',
      color: 'bg-teal-100 text-teal-600'
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Real-Time Sync',
      description: 'All data syncs instantly across devices. Start on mobile, finish on desktop seamlessly.',
      color: 'bg-amber-100 text-amber-600'
    },
    {
      icon: <Cloud className="w-8 h-8" />,
      title: 'Cloud Storage',
      description: 'Secure cloud storage for all recordings and medical data with automatic backup.',
      color: 'bg-cyan-100 text-cyan-600'
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-dark-bg-primary">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-orange-50 dark:from-dark-bg-secondary dark:via-dark-bg-primary dark:to-dark-bg-tertiary pt-16 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text Content */}
            <div className="text-center lg:text-left">
              {/* Logo */}
              <div className="flex justify-center lg:justify-start mb-8">
                <img 
                  src="/logo_clear_back.png" 
                  alt={`${APP_NAME} Logo - AI Veterinary Voice Agent`}
                  loading="lazy"
                  className="w-32 h-32 object-contain"
                />
              </div>

              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 dark:text-dark-text-primary mb-4 leading-tight">
                Download <span className="text-blue-600 dark:text-blue-400">the {APP_NAME}</span> app on your mobile device!
              </h1>
              
              <div className="w-20 h-1 bg-blue-600 mb-6 mx-auto lg:mx-0"></div>

              <p className="text-xl text-gray-600 dark:text-dark-text-secondary mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Our free, intuitive, and easy-to-use app helps you manage your veterinary practice from the palm of your hand.
              </p>

              {/* App Store Badge */}
              <div className="flex justify-center lg:justify-start">
                <a
                  href="https://apps.apple.com/us/app/helppetai/id6751992263"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block hover:opacity-80 transition-opacity"
                  onClick={() => trackAppDownload('iphone_page_hero')}
                >
                  <img
                    src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&amp;releaseDate=1697673600"
                    alt="Download on the App Store"
                    className="h-14"
                  />
                </a>
              </div>
            </div>

            {/* Right Column - Vet Using iPhone */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-orange-400 rounded-3xl blur-3xl opacity-20"></div>
                <img
                  src="/vetiphone.jpg"
                  alt={`Veterinarian using ${APP_NAME} iPhone App`}
                  loading="lazy"
                  className="relative w-full max-w-md rounded-3xl shadow-2xl object-cover h-[600px]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Everything You Need in Your Pocket
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {APP_NAME} mobile app brings powerful practice management tools to your iPhone
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className={`w-16 h-16 rounded-xl ${feature.color} flex items-center justify-center mb-4`}>
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to Transform Your Practice?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join hundreds of veterinarians using {APP_NAME} to streamline their workflow
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://apps.apple.com/us/app/helppetai/id6751992263"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block hover:opacity-80 transition-opacity"
              onClick={() => trackAppDownload('iphone_page_cta')}
            >
              <img
                src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/white/en-us?size=250x83&amp;releaseDate=1697673600"
                alt="Download on the App Store"
                className="h-14"
              />
            </a>
            <Link
              to="/signup"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-50 transition-all inline-flex items-center justify-center gap-2"
              onClick={() => trackGetStarted('iphone_page')}
            >
              Create Free Account
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IPhoneApp;

